import type { ReactNode } from "react";
import { useNativeHeaderHidden } from "@/app/nativeTitle";
import { useBootstrap } from "@/app/providers";
import { ScreenHeader } from "@/components/ui/ScreenHeader";

export function ChangeAddressResult({
  screenTitle,
  showHeader = true,
  icon,
  status,
  statusTone,
  heading,
  description,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
}: {
  screenTitle: string;
  showHeader?: boolean;
  icon: ReactNode;
  status: string;
  statusTone: "success" | "error";
  heading: string;
  description?: string;
  actionLabel: string;
  onAction: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}) {
  const { data } = useBootstrap();

  // Only the native shell owns a header to take away. In web mode the shell has
  // already stood its header down, and asking for it back on the way out would
  // put a second bar above the one this app draws.
  useNativeHeaderHidden(!showHeader && data.headerMode === "native");

  return (
    <>
      {showHeader ? <ScreenHeader title={screenTitle} /> : null}
      <main className="flex flex-1 flex-col items-center bg-white px-8 pb-[calc(180px+env(safe-area-inset-bottom))] tracking-[-0.011em]">
        <div className="flex flex-1 flex-col items-center justify-center">
          {icon}
          <p
            className={`text-20 mt-6 text-center font-bold ${
              statusTone === "success" ? "text-success" : "text-error"
            }`}
          >
            {status}
          </p>
          <h1 className="text-24 mt-2 text-center font-bold">{heading}</h1>
          {description === undefined ? null : (
            <p className="text-16 text-field-label mt-3 text-center leading-6 font-light">
              {description}
            </p>
          )}
        </div>
      </main>
      <div className="fixed inset-x-0 bottom-0 flex flex-col gap-3 bg-white px-[30px] pb-4">
        <button
          type="button"
          onClick={onAction}
          className="text-24 h-[51px] w-full rounded-[10px] bg-primary text-white"
        >
          {actionLabel}
        </button>
        {secondaryLabel === undefined || onSecondary === undefined ? null : (
          <button
            type="button"
            onClick={onSecondary}
            className="text-24 h-[51px] w-full rounded-[10px] border-2 border-primary bg-white text-primary"
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </>
  );
}

export function SuccessCheckIcon() {
  return (
    <svg viewBox="0 0 160 160" className="h-40 w-40 shrink-0" aria-hidden="true">
      <circle
        cx="80"
        cy="80"
        r="76"
        fill="none"
        className="stroke-light-gray-1"
        strokeWidth="6"
      />
      <circle cx="80" cy="80" r="52" className="fill-light-gray-1" />
      <path
        d="M56 81.5 72 97.5 106 63.5"
        fill="none"
        className="stroke-success"
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ErrorWarningIcon() {
  return (
    <svg viewBox="0 0 160 160" className="h-40 w-40 shrink-0" aria-hidden="true">
      <circle cx="80" cy="80" r="52" className="fill-light-gray-1" />
      <path
        d="M80 48 112 108H48Z"
        className="fill-error"
        strokeLinejoin="round"
      />
      <rect x="76" y="68" width="8" height="22" rx="2" fill="white" />
      <circle cx="80" cy="98" r="4" fill="white" />
    </svg>
  );
}
